import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Stack,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import {
    Print as IconPrint,
    PictureAsPdf as IconPdf,
    TableView as IconExcel,
    Description as IconWord,
    ArrowBack as IconVolver,
    Assessment as IconReport
} from '@mui/icons-material';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { getsKardex, getsArticulo } from '../../services/inventarioService';
import FiltroFilial from '../../components/common/FiltroFilial';
import { useFiltroFilial } from '../../hooks/useFiltroFilial';
import { exportarExcel, exportarPdf, exportarWord, imprimirReporte } from '../../services/reporteExportService';
import { registrarEjecucionReporte } from '../../services/bitacoraReporteService';

const ReporteKardex = () => {
    const { filiales, idFilial, setIdFilial, bloqueado } = useFiltroFilial();

    const [articulos, setArticulos] = useState([]);
    const [idArticulo, setIdArticulo] = useState(0);
    const [tipoReporte, setTipoReporte] = useState('KARDEX_VALORIZADO'); // KARDEX_VALORIZADO, INGRESOS, SALIDAS
    const [fechaInicio, setFechaInicio] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [fechaFin, setFechaFin] = useState(moment().endOf('month').format('YYYY-MM-DD'));

    const [rows, setRows] = useState([]);
    const [cargando, setCargando] = useState(false);

    useEffect(() => {
        getsArticulo({ idFilial: idFilial || 0, soloActivos: true })
            .then((data) => setArticulos(data || []))
            .catch((err) => console.error(err));
    }, [idFilial]);

    const generarReporte = useCallback(async () => {
        setCargando(true);
        const t0 = performance.now();
        try {
            const data = await getsKardex({
                idFilial: idFilial || 0,
                idArticulo: idArticulo || 0,
                fechaInicio: fechaInicio ? moment(fechaInicio).toISOString() : null,
                fechaFin: fechaFin ? moment(fechaFin).toISOString() : null,
                estado: 'APROBADO'
            });

            let filtrados = data || [];
            if (tipoReporte === 'INGRESOS') {
                filtrados = filtrados.filter((r) => r.cantidadEntrada > 0);
            } else if (tipoReporte === 'SALIDAS') {
                filtrados = filtrados.filter((r) => r.cantidadSalida > 0);
            }

            setRows(filtrados);

            registrarEjecucionReporte({
                reportPath: '/inventario/reportes',
                format: 'HTML',
                reportAction: 'Render',
                timeRendering: Math.round(performance.now() - t0),
                rowCount: filtrados.length,
                status: 'rsSuccess'
            });
        } catch (err) {
            console.error('Error generando reporte kardex:', err);
        } finally {
            setCargando(false);
        }
    }, [idFilial, idArticulo, tipoReporte, fechaInicio, fechaFin]);

    useEffect(() => {
        generarReporte();
    }, [generarReporte]);

    // Resumen estadístico
    const totalEntradaCant = rows.reduce((acc, r) => acc + (r.cantidadEntrada || 0), 0);
    const totalEntradaMonto = rows.reduce((acc, r) => acc + (r.totalEntrada || 0), 0);
    const totalSalidaCant = rows.reduce((acc, r) => acc + (r.cantidadSalida || 0), 0);
    const totalSalidaMonto = rows.reduce((acc, r) => acc + (r.totalSalida || 0), 0);
    const ultimoSaldoCant = rows.length > 0 ? rows[0].cantidadSaldo : 0;
    const ultimoSaldoMonto = rows.length > 0 ? rows[0].totalSaldo : 0;

    const columnasExport = [
        { header: 'Fecha', key: 'fecha' },
        { header: 'Filial', key: 'filial' },
        { header: 'Artículo', key: 'articulo' },
        { header: 'Tipo Mov.', key: 'tipoMovimiento' },
        { header: 'Documento', key: 'documentoSustento' },
        { header: 'Entrada Cant', key: 'cantidadEntrada' },
        { header: 'Entrada Total', key: 'totalEntrada' },
        { header: 'Salida Cant', key: 'cantidadSalida' },
        { header: 'Salida Total', key: 'totalSalida' },
        { header: 'Saldo Cant', key: 'cantidadSaldo' },
        { header: 'Saldo Valor', key: 'totalSaldo' }
    ];

    const filasExport = rows.map((r) => ({
        fecha: moment(r.fechaMovimiento).format('DD/MM/YYYY HH:mm'),
        filial: r.filial,
        articulo: `${r.codigoArticulo} - ${r.nombreArticulo}`,
        tipoMovimiento: r.tipoMovimiento,
        documentoSustento: r.documentoSustento,
        cantidadEntrada: Number(r.cantidadEntrada).toFixed(2),
        totalEntrada: Number(r.totalEntrada).toFixed(2),
        cantidadSalida: Number(r.cantidadSalida).toFixed(2),
        totalSalida: Number(r.totalSalida).toFixed(2),
        cantidadSaldo: Number(r.cantidadSaldo).toFixed(2),
        totalSaldo: Number(r.totalSaldo).toFixed(2)
    }));

    const tituloReporte =
        tipoReporte === 'KARDEX_VALORIZADO' ? 'Reporte de Kardex Valorizado y Físico (Promedio Ponderado)' :
        tipoReporte === 'INGRESOS' ? 'Reporte Detallado de Ingresos y Compras de Materiales' :
        'Reporte Detallado de Salidas y Consumos de Materiales';

    const handleImprimir = () => {
        imprimirReporte({
            titulo: tituloReporte,
            subtitulo: `Periodo: ${moment(fechaInicio).format('DD/MM/YYYY')} al ${moment(fechaFin).format('DD/MM/YYYY')}`,
            columnas: columnasExport,
            filas: filasExport
        });
    };

    const handlePdf = () => {
        exportarPdf({
            nombreArchivo: `Reporte_${tipoReporte}_${moment().format('YYYYMMDD')}.pdf`,
            titulo: tituloReporte,
            subtitulo: `Periodo: ${moment(fechaInicio).format('DD/MM/YYYY')} al ${moment(fechaFin).format('DD/MM/YYYY')}`,
            columnas: columnasExport,
            filas: filasExport
        });
    };

    const handleExcel = () => {
        exportarExcel({
            nombreArchivo: `Reporte_${tipoReporte}_${moment().format('YYYYMMDD')}.xlsx`,
            hojas: [{ nombre: 'Kardex', columnas: columnasExport, filas: filasExport }]
        });
    };

    const handleWord = () => {
        exportarWord({
            nombreArchivo: `Reporte_${tipoReporte}_${moment().format('YYYYMMDD')}.docx`,
            titulo: tituloReporte,
            columnas: columnasExport,
            filas: filasExport
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Cabecera */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            component={Link}
                            to="/inventario/kardex"
                            startIcon={<IconVolver />}
                            size="small"
                            sx={{ textTransform: 'none' }}
                        >
                            Volver a Movimientos
                        </Button>
                    </Stack>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1f2937', mt: 1 }}>
                        <IconReport sx={{ verticalAlign: 'middle', mr: 1, color: '#1976d2' }} />
                        {tituloReporte}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Control físico y valorizado oficial de existencias y consumos de materiales por sede.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<IconPrint />} onClick={handleImprimir} size="small">
                        Imprimir
                    </Button>
                    <Button variant="outlined" color="error" startIcon={<IconPdf />} onClick={handlePdf} size="small">
                        PDF
                    </Button>
                    <Button variant="outlined" color="success" startIcon={<IconExcel />} onClick={handleExcel} size="small">
                        Excel
                    </Button>
                    <Button variant="outlined" color="primary" startIcon={<IconWord />} onClick={handleWord} size="small">
                        Word
                    </Button>
                </Stack>
            </Stack>

            {/* Filtros */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#ffffff' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <FiltroFilial
                            filiales={filiales}
                            idFilial={idFilial}
                            setIdFilial={setIdFilial}
                            bloqueado={bloqueado}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo de Reporte</InputLabel>
                            <Select
                                value={tipoReporte}
                                onChange={(e) => setTipoReporte(e.target.value)}
                                label="Tipo de Reporte"
                            >
                                <MenuItem value="KARDEX_VALORIZADO">Kardex Físico y Valorizado</MenuItem>
                                <MenuItem value="INGRESOS">Solo Ingresos / Compras</MenuItem>
                                <MenuItem value="SALIDAS">Solo Salidas / Consumos</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Artículo Específico</InputLabel>
                            <Select
                                value={idArticulo}
                                onChange={(e) => setIdArticulo(e.target.value)}
                                label="Artículo Específico"
                            >
                                <MenuItem value={0}>-- Todos los Artículos --</MenuItem>
                                {articulos.map((a) => (
                                    <MenuItem key={a.id} value={a.id}>
                                        {a.codigo} - {a.nombre}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={1.5}>
                        <TextField
                            label="Desde"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={1.5}>
                        <TextField
                            label="Hasta"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Tarjetas Resumen */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ bgcolor: '#f0fdf4' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                TOTAL ENTRADAS / COMPRAS
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#15803d' }}>
                                S/ {totalEntradaMonto.toFixed(2)} ({totalEntradaCant.toFixed(2)} und)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ bgcolor: '#fef2f2' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                TOTAL SALIDAS / CONSUMOS
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#b91c1c' }}>
                                S/ {totalSalidaMonto.toFixed(2)} ({totalSalidaCant.toFixed(2)} und)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ bgcolor: '#eff6ff' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                {idArticulo > 0 ? 'SALDO FINAL DISPONIBLE' : 'VALOR ESTIMADO EN MOVIMIENTOS'}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1d4ed8' }}>
                                {idArticulo > 0 ? `${Number(ultimoSaldoCant).toFixed(2)} und (S/ ${Number(ultimoSaldoMonto).toFixed(2)})` : `S/ ${(totalEntradaMonto - totalSalidaMonto).toFixed(2)}`}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabla Detallada */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 650 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                <TableCell sx={{ fontWeight: 'bold' }} rowSpan={2}>Fecha / Hora</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} rowSpan={2}>Sede</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} rowSpan={2}>Artículo / Insumo</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} rowSpan={2}>Movimiento</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} rowSpan={2}>Doc. Sustento</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e2e8f0', textAlign: 'center' }} colSpan={2}>Entradas</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e2e8f0', textAlign: 'center' }} colSpan={2}>Salidas</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e2e8f0', textAlign: 'center' }} colSpan={2}>Saldos Resultantes</TableCell>
                            </TableRow>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Cant.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Total (S/)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Cant.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Total (S/)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Cant.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Total (S/)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cargando ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        No hay movimientos para este criterio.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((r) => (
                                    <TableRow key={r.id} hover>
                                        <TableCell>{moment(r.fechaMovimiento).format('DD/MM/YYYY HH:mm')}</TableCell>
                                        <TableCell>{r.filial}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.nombreArticulo}</Typography>
                                            <Typography variant="caption" color="text.secondary">{r.codigoArticulo}</Typography>
                                        </TableCell>
                                        <TableCell>{r.tipoMovimiento}</TableCell>
                                        <TableCell>{r.documentoSustento}</TableCell>
                                        <TableCell align="right" sx={{ color: '#16a34a' }}>
                                            {r.cantidadEntrada > 0 ? Number(r.cantidadEntrada).toFixed(2) : '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#16a34a' }}>
                                            {r.totalEntrada > 0 ? Number(r.totalEntrada).toFixed(2) : '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#dc2626' }}>
                                            {r.cantidadSalida > 0 ? Number(r.cantidadSalida).toFixed(2) : '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#dc2626' }}>
                                            {r.totalSalida > 0 ? Number(r.totalSalida).toFixed(2) : '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            {Number(r.cantidadSaldo).toFixed(2)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            S/ {Number(r.totalSaldo).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default ReporteKardex;
